import React, { ChangeEvent, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CategoryItemNode, IdAndName } from '../../../shared/types';
import {
	Loader, Option, Select, TextArea, TextField,
	Heading, TextBlock, Checkbox
} from 'neon-design-system';
import { useQuery } from '@apollo/react-hooks';
import { makeStyles, createStyles } from '@material-ui/core';
import { ADD_CATEGORY, CATEGORIES_TREE, DELETE_CATEGORY, EDIT_CATEGORY, GET_PROMOTIONS_SHORT } from '../../../gql/categories';
import { useConstrainTextArea, useFormControl, useFormGroup, useManageTemplateForm } from '../../../utils/form.utils';
import { getCategoryItemFromTree, getCategoriesAsList } from '../../../utils/list-utils';

import { requiredValidator } from '../../../utils/validator.utils';
import ManageTemplate, { ManageTemplateInstance } from '../../ui/ManageTemplate';
import ManageTemplateFormItem from '../../ui/ManageTemplateFormItem';
import { sortArray } from '../../../utils/sorting.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { premiumChannelsEnabled, topicsOrCategories } from '../../../utils/utils';

const returnListUrl = premiumChannelsEnabled() ? baseUrls.publisher.topics.list : baseUrls.publisher.categories.list;

interface RequestVariables {
	name: string;
	parent: string;
	description: string;
	version: string;
}

const useStyles = makeStyles(theme => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: 1200
	},
	imageUploadInput: {
		cursor: 'pointer !important'
	},
	sectorCard: {
		backgroundColor: theme.palette.grey[800],
		borderRadius: 4,
		height: 120,
		padding: theme.spacing(2)
	},
	featuredCaption: {
		marginBottom: theme.spacing(1)
	},
	featuredText: {
		marginBottom: theme.spacing(2)
	}

}));



export const CategoryAddEdit = (): JSX.Element => {
	const { _id } = useParams();
	const classes = useStyles();

	const { loading, data } = useQuery(CATEGORIES_TREE, {
		fetchPolicy: 'network-only',
		onCompleted: (data) => {

			const tmpTreeData = (data?.categoriesAsTree as CategoryItemNode[]) ?? [];
			const _editItem = getCategoryItemFromTree(tmpTreeData, _id);

			if (!_editItem) {
				parentOptions.current = getPotentialParentOptions(undefined, tmpTreeData);
				const parentOpt = parentOptions.current.find(x => x.value === 0);
				parentCategoryFormControl?.setInitialValue(parentOpt);
				imageSmallFormControl.setInitialValue('');
				imageLargeFormControl.setInitialValue('');
				isFeaturedFormControl.setInitialValue(false);
				return;
			}

			nameFormControl?.setInitialValue(_editItem.name);
			descriptionFormControl?.setInitialValue(_editItem.description);
			constrainTextArea?.setCurrentChars(_editItem?.description?.length)
			imageSmallFormControl?.setInitialValue(_editItem.smallImage);
			imageLargeFormControl?.setInitialValue(_editItem.largeImage);
			isFeaturedFormControl.setInitialValue(_editItem.isFeatured);


			parentOptions.current = getPotentialParentOptions(_id, tmpTreeData);

			const parentOpt = parentOptions.current.find(x => x.value === (_editItem.parent || 0));
			parentCategoryFormControl?.setInitialValue(parentOpt);
		}
	});

	const treeData = (data?.categoriesAsTree as CategoryItemNode[]) ?? [];
	const editItem = getCategoryItemFromTree(treeData, _id);

	const nameFormControl = useFormControl<string>('', [requiredValidator]);
	const imageSmallFormControl = useFormControl<string>('', []);
	const imageLargeFormControl = useFormControl<string>('', []);
	const isFeaturedFormControl = useFormControl<boolean>(false, [requiredValidator]);

	const parentOptions = useRef<Option[]>(
		getPotentialParentOptions(_id, [])
	);

	const parentCategoryFormControl = useFormControl<Option | undefined>(
		parentOptions.current.find(po => po.value === 0), [requiredValidator]
	);
	const descriptionFormControl = useFormControl<string>('', [requiredValidator]);

	const queryPromotionsList = useQuery(GET_PROMOTIONS_SHORT, { fetchPolicy: 'network-only' });

	const promList: IdAndName[] = queryPromotionsList.loading ? [] : sortArray(queryPromotionsList.data?.promotionalContent ?? [], 'name')
	const promotionsOptions = getPromotionsOptions(promList);

	const promotionsFormControl = useFormControl<Option | undefined>(undefined, []
	);

	const constrainTextArea = useConstrainTextArea(0, 500);

	const form = useFormGroup({
		name: nameFormControl,
		description: descriptionFormControl,
		parent: parentCategoryFormControl,
		smallImage: imageSmallFormControl,
		largeImage: imageLargeFormControl,
		isFeatured: isFeaturedFormControl,
		promotionalContentId: promotionsFormControl
	});

	const { isNew, formMessage, formBusy, onSubmit, onCancel, onDelete, onAlertBoxClose } = useManageTemplateForm(
		ManageTemplateInstance.Category,
		form,
		editItem,
		ADD_CATEGORY,
		EDIT_CATEGORY,
		DELETE_CATEGORY,
		returnListUrl
	);


	if (loading || queryPromotionsList.loading) return <Loader />;

	if (editItem?.promotionalContentId
		&& !promotionsFormControl.isChanged
		&& editItem.promotionalContentId !== (promotionsFormControl?.value ? promotionsFormControl.value.value : '')) {
		promotionsFormControl?.setInitialValue(promotionsOptions.find(po => po.value === editItem.promotionalContentId));
	}

	if (data && isNew) {
		parentOptions.current = getPotentialParentOptions(_id, treeData);
	}

	const getReqBody = (): RequestVariables => {

		return {
			...form.value,
			parent: form.value.parent || undefined,
			promotionalContentId: form.value.promotionalContentId || null,
			version: editItem?.version || undefined,
			isLocked: editItem?.isLocked || false,
			isPremium: editItem?.isPremium || false
		}
	};

	const isRoot = (!parentCategoryFormControl || parentCategoryFormControl?.value?.value === 0);

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<ManageTemplate
					dialogMessage={<>You are about to delete the article
						`${topicsOrCategories()}` <strong>{nameFormControl?.value}</strong>.
						This action cannot be undone, so you must be sure and confirm.</>}
					onDelete={(): void => {
						onDelete(nameFormControl?.value);
					}}
					pathArea={topicsOrCategories(true, true)}
					returnUrl={returnListUrl}
					templateInstance={premiumChannelsEnabled() ? ManageTemplateInstance.Topic : ManageTemplateInstance.Category}
					isNew={isNew}
					formMessage={formMessage}
					formBusy={formBusy}
					onCancel={onCancel}
					onSubmit={(ev: any): void => {
						onSubmit(ev, getReqBody());
					}}
					onAlertBoxClose={onAlertBoxClose}>
					<ManageTemplateFormItem>
						<TextField
							id={'category-name-input'}
							fullWidth
							error={nameFormControl?.showError}
							errorText={nameFormControl?.validationMessage}
							type="text"
							label="Name"
							autoComplete="off"
							value={nameFormControl?.value}
							onBlur={nameFormControl?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								if (val?.length > 100) {
									return;
								}
								nameFormControl?.setValue(val);
							}}
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<Select
							id={'category-parent-select'}
							size="medium"
							clearable={false}
							menuPlacement="bottom"
							label={`Parent ${topicsOrCategories(true)}`}
							error={parentCategoryFormControl?.showError}
							errorText={parentCategoryFormControl?.validationMessage}
							value={parentCategoryFormControl?.value}
							onBlur={parentCategoryFormControl?.handleBlur}
							onChange={(opt: Option): void => {
								parentCategoryFormControl?.setValue(opt);
								if (opt.value !== 0) {
									isFeaturedFormControl?.setValue(false);
								}
							}}
							options={parentOptions.current} />
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextArea
							id={'category-description-textarea'}
							fullWidth
							type="text"
							label="Description"
							error={descriptionFormControl?.showError}
							errorText={descriptionFormControl?.validationMessage}
							progressBarHelperText={constrainTextArea?.charsMessage}
							autoComplete="off"
							rowsMax={4}
							value={descriptionFormControl?.value}
							showProgressBar={true}
							progressBarIndicator={constrainTextArea?.currentChars}
							progressBarLimit={constrainTextArea?.charsLimit}
							onBlur={descriptionFormControl?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								if (val?.length > constrainTextArea?.charsLimit) {
									return;
								}
								constrainTextArea?.setCurrentChars(val?.length)
								descriptionFormControl?.setValue(val);
							}}
						/>
					</ManageTemplateFormItem>

					<ManageTemplateFormItem>
						<TextField
							id={'category-small-image'}
							onChange={(ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => imageSmallFormControl?.setValue(ev?.target?.value)}
							onBlur={imageSmallFormControl?.handleBlur}
							label="Card Image"
							helperText="Choose the small crop version of the image"
							placeholder="filename-small.jpg"
							autoComplete="off"
							error={imageSmallFormControl?.showError}
							errorText={imageSmallFormControl?.validationMessage}
							value={imageSmallFormControl?.value}
							className={classes.imageUploadInput} />
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextField
							id={'category-large-image'}
							onChange={(ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => imageLargeFormControl?.setValue(ev?.target?.value)}
							onBlur={imageLargeFormControl?.handleBlur}
							label="Cover image"
							helperText="Choose the wide crop version of the image"
							placeholder="filename-large.jpg"
							autoComplete="off"
							error={imageLargeFormControl?.showError}
							errorText={imageLargeFormControl?.validationMessage}
							value={imageLargeFormControl?.value}
							className={classes.imageUploadInput} />
					</ManageTemplateFormItem>

					<ManageTemplateFormItem>
						<Select
							id={'category-promotion-select'}
							size="medium"
							clearable
							menuPlacement="bottom"
							label="Promotional content"
							placeholder="Select an item from the list..."
							error={promotionsFormControl?.showError}
							errorText={promotionsFormControl?.validationMessage}
							helperText="Choose a relevant promotion to be displayed alongside the article"
							value={promotionsFormControl?.value}
							onBlur={promotionsFormControl?.handleBlur}
							onChange={(opt: Option): void => {
								promotionsFormControl?.setValue(opt);
							}}
							options={promotionsOptions} />
					</ManageTemplateFormItem>

					{isRoot &&
						<div className={classes.sectorCard}>
							<Heading styleName="headline3" className={classes.featuredCaption}>{topicsOrCategories(false, true)} as Sector</Heading>
							<TextBlock styleName="footnote" className={classes.featuredText}> A {topicsOrCategories()} can be flagged as a Sector, hence it will be shown in the "Browse by sector" sections.</TextBlock>
							<Checkbox
								id="category-sector-checkbox"
								label={`This ${topicsOrCategories()} is a sector`}
								checked={isFeaturedFormControl?.value}
								value="a"
								disabled={!isRoot}
								onChange={(e: ChangeEvent<any>): void => {
									isFeaturedFormControl?.setValue(e?.target?.checked);
								}} />
						</div>
					}

				</ManageTemplate>
			</div>
		</div>
	);
};

function getPromotionsOptions(items: IdAndName[]): Option[] {
	return [...items.map(x => (({ label: x.name, value: x._id } as Option)))];
}

function getPotentialParentOptions(id: string | undefined, categories: CategoryItemNode[]): Option[] {

	const noParentOption: Option = { label: 'No parent', value: 0 };

	if (!categories) {
		return [noParentOption];
	}

	// as parent we get everything except this Category and its children
	const list = getCategoriesAsList(categories, id ? [id] : []);

	const filtered = sortArray(list, 'name').map(x => (({ label: x.name, value: x._id } as Option)));
	return [noParentOption, ...filtered];

}

export default CategoryAddEdit;
